import React, { useState, useContext, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { IconButton, Typography, Grid, Tooltip } from '@material-ui/core';
import { Dialog, DialogContent, DialogTitle, CardMedia } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/CancelSharp';
import { makeStyles } from '@material-ui/core/styles';
import mixpanel from 'mixpanel-browser';

import { StitchIcon } from './Icons';

import { useThumbnailUrls, useMaterials, useMaterialsApi } from '../utils/materialsLib';
import { AppContext } from './AppState';


const useStyles = makeStyles((theme) => ({
  modal: {
    minHeight: '300px',
    minWidth: '730px',
  },
  gradient: {
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'right',
    color: theme.palette.secondary.light,
    paddingRight: theme.spacing(2),
    background: theme.palette.secondary.main,
  },
  closeButton: {
    float: 'right',
    color: 'grey',
    marginTop: theme.spacing(-1),
  },
  iconButton: {
    marginLeft: theme.spacing(1),
  },
  stitchButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  subHeading: {
    marginTop: theme.spacing(2),
  },
  thumbnailGrid: {
    marginBottom: theme.spacing(3),
    //maxHeight: '320px'
  },
  image: {
    height: 65,
    width: 105,
  },
  clickable: {
    cursor: 'pointer',
  },
  unclickable: {
    border: '1px solid #ccc',
  },
  alignCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  helpIconInline: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropDown: {
    marginTop: theme.spacing(2),
  },
  invalidText: {
    color: 'red',
    float: 'right',
    marginRight: theme.spacing(0.3),
  },
  nameText: {
    minWidth: '320px',
  },
  nudgeDown: {
    marginTop: theme.spacing(2),
  },
  fullWidth: {
    width: '100%',
  },
  regularWidth: {
  },

}));

export default function StitchViewModal(props) {
  const { stitchedMaterialProxy, visible, onHide, onNavigate } = props;

  // A material proxy is used to avoid creating a dependency on the entire material. The proxy material only
  // has the materialUserId and materialId fields set. materialUserId should be set to the userId that owns
  // the material.
  const { materialUserId, materialId } = stitchedMaterialProxy;

  const classes = useStyles();
  const [inputMaterials, setInputMaterials] = useState([]);
  const [gridSize, setGridSize] = useState([0, 0]);

  const {
    setGalleryMaterials, setFilteredMaterials, setGalleryId, setRedirect,
    filters, setFilters, retainFiltersExclude, setRetainFiltersExclude, qaDisplay,
  } = useContext(AppContext);

  const { user } = useAuth0();

  const { materials } = useMaterials(materialUserId, null, null, false, qaDisplay, false, 0);
  const stitchedMaterial = materials?.find(m => m.materialId === materialId);

  const { getStitchedInputs } = useMaterialsApi();

  function sortByName(materials, gridSize) {
    var matsOut = [];
    for (var y = gridSize[1] - 1; y >= 0; y--) {
      for (var x = 0; x < gridSize[0]; x++) {
        const suffix = String.fromCharCode(65 + x) + String(gridSize[0] - y);
        const findMat = materials.filter(mat => mat.name?.slice(-2) === suffix);
        if (findMat.length === 1) {
          matsOut.push(findMat[0]);
        }
      }
    }
    if (matsOut.length < materials.length) {
      // If not every material could be matched by name, just don't change the order
      matsOut = materials;
    }
    return matsOut;
  }

  useEffect(() => {
    async function validateInputs() {
      mixpanel.track('View stitch modal opened', { 'Material': stitchedMaterial.materialId });
      const res = await getStitchedInputs(stitchedMaterial.userId, stitchedMaterial.materialId);
      if (res) {
        const { materialIds, materialIdsGrid, gridSize } = res;
        const mids = materialIdsGrid ? materialIdsGrid.flat() : materialIds;
        var mats = [];
        for (const mid of mids) {
          const res = materials.filter(mat => mat.materialId === mid);
          mats.push(res.length > 0 ? res[0] : {});
        }
        if (!materialIdsGrid) {
          // If the metadata only has a flat (unsorted) list of materials, try to sort them by name
          mats = sortByName(mats, gridSize);
        }

        setInputMaterials(mats);
        setGridSize(gridSize);
      }
    }
    if (visible && stitchedMaterial && materials && user) {
      validateInputs();
    }
  }, [visible, stitchedMaterial, materials, user, getStitchedInputs]);

  const { thumbnailUrls } = useThumbnailUrls(stitchedMaterial?.userId, inputMaterials, 'StitchViewModal');

  if (!stitchedMaterial) return null;

  const inputMids = inputMaterials ? inputMaterials.map(mat => mat ? mat.materialId : null) : [];
  const thumbnails = inputMids.map(mid => mid ? thumbnailUrls[mid] : '');
  const matsPerRow = gridSize?.[0];
  // FIXME: Convert to child component.
  const imageCode = (visible && inputMaterials?.length > 0 && matsPerRow > 0) ? (
    <Grid container spacing={1} className={classes.thumbnailGrid}>
    {thumbnails.map((t, i) =>
      <React.Fragment key={i}>
        <Grid item className={classes.regularWidth}>
          <Tooltip title={inputMids[i] ? inputMaterials[i].name : 'input not available'}>
            <CardMedia
              className={`${classes.image} ${inputMids[i] ? classes.clickable : classes.unclickable}`}
              image={t}
              component={t ? undefined : 'div'}
              onClick={() => inputMids[i] && handleClick(inputMids[i])}
            />
          </Tooltip>
        </Grid>
        {i % matsPerRow === matsPerRow - 1 && (<div className={classes.fullWidth} />)}
      </React.Fragment>
    )}
    </Grid>
  ) : '';

  function handleClick(materialId) {
    setGalleryId(materialId);
    setGalleryMaterials(inputMaterials);
    setFilteredMaterials(inputMaterials);
    setFilters({ ...filters, showStitchInputs: true });
    setRetainFiltersExclude([...retainFiltersExclude, 'showStitchInputs']);
    if (onNavigate) {
      onNavigate();
    }
    setRedirect('/view');
  }

  return (<>
    <Dialog classes={{paper: classes.modal}} centered='true' open={visible} onClose={onHide}>
      <div className={classes.gradient}>
      </div>
      <DialogTitle>
        {stitchedMaterial.name}
        <IconButton onClick={onHide} className={classes.closeButton}>
          <CancelIcon />
        </IconButton>
        <Typography>
          <StitchIcon /> Stitched material {inputMaterials.length > 0 && `from ${inputMaterials.length} inputs (${gridSize[0]} x ${gridSize[1]})`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container>
          <Grid item xs={12}>
            {imageCode}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  </>);
}
